"use client";
import { createContext, useContext, useState, ReactNode } from 'react';

export interface CartItem {
  id: string;
  name: string;
  brand: string;
  price: string;
  totalPrice: string;
  image: string;
  quantity: number;
  paymentType: 'avista' | 'parcelado';
  shippingPrice: string;
  stock: number;
  reserved?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string, paymentType: 'avista' | 'parcelado') => void;
  updateQuantity: (id: string, paymentType: 'avista' | 'parcelado', quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = (item: Omit<CartItem, 'quantity'>) => {
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        i => i.id === item.id && i.paymentType === item.paymentType
      );

      if (existingItemIndex > -1) {
        const newItems = [...prevItems];
        if (newItems[existingItemIndex].quantity < item.stock) {
          newItems[existingItemIndex].quantity += 1;
        }
        return newItems;
      }

      return [...prevItems, { ...item, quantity: 1 }];
    });
  };

  const removeItem = (id: string, paymentType: 'avista' | 'parcelado') => {
    setItems(prevItems =>
      prevItems.filter(item => !(item.id === id && item.paymentType === paymentType))
    );
  };

  const updateQuantity = (id: string, paymentType: 'avista' | 'parcelado', quantity: number) => {
    if (quantity <= 0) {
      removeItem(id, paymentType);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === id && item.paymentType === paymentType) {
          const newQuantity = quantity > item.stock ? item.stock : quantity;
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        isOpen,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
